/**
 * @file meetingListLogic.ts
 * @brief Date-range presets and the tag save for the Boardrooms list.
 *
 * @details Split from meetingListTools.tsx so that file exports components
 * only (fast refresh). Rules are the phone's (BoardroomSessionsScreen.tsx).
 *
 * @author DES
 * @date 30-09-2026
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { apiPatch } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { companyMeetingsKeys } from "@/features/company-meetings/api";

/** The phone's three, plus Yesterday and Last month for the desktop picker. */
export type RangePreset = "today" | "yesterday" | "week" | "month" | "lastMonth" | "custom";

export function rangeFor(preset: Exclude<RangePreset, "custom">, now = new Date()) {
  if (preset === "today") return { from: startOfDay(now), to: endOfDay(now) };
  if (preset === "yesterday") {
    const y = subDays(now, 1);
    return { from: startOfDay(y), to: endOfDay(y) };
  }
  if (preset === "lastMonth") {
    const m = subMonths(now, 1);
    return { from: startOfMonth(m), to: endOfMonth(m) };
  }
  // The last seven days, today included (the phone's rule, 28-09-2026).
  if (preset === "week") return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
  return { from: startOfMonth(now), to: endOfMonth(now) };
}

export function rangeLabel(preset: RangePreset, from: Date, to: Date): string {
  if (preset === "today") return "Today";
  if (preset === "yesterday") return "Yesterday";
  if (preset === "lastMonth") return "Last month";
  if (preset === "week") return "Past 7 days";
  if (preset === "month") return "This month";
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${format(from, sameYear ? "d MMM" : "d MMM yyyy")} – ${format(to, sameYear ? "d MMM" : "d MMM yyyy")}`;
}

/** The desktop picker's presets, in the order it lists them. */
export const DATE_PRESET_ORDER: Exclude<RangePreset, "custom">[] = [
  "today",
  "yesterday",
  "week",
  "month",
  "lastMonth",
];

export function datePresetDefs() {
  return DATE_PRESET_ORDER.map((id) => ({
    id,
    label: rangeLabel(id, new Date(), new Date()),
    range: () => rangeFor(id),
  }));
}

// Owner only: the server refuses anyone else (PATCH /sessions/:id/tags).
export function useSaveMeetingTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      apiPatch<unknown>(`${boardroomEndpoints.byId(id)}/tags`, { tags }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
    },
  });
}
